import React, { useEffect, useState } from "react";
import Popup from "reactjs-popup";
import { FormControl, ControlLabel, Button } from "react-bootstrap";
import { updateRepoData } from "../../libs/API";
import confirmMarkBillable from "./confirmBillable";

export default function EditRepositoryPopup({
  show,
  onHide,
  repository,
  departments,
  onSaved,
}) {
  const [repositoryOwner, setRepositoryOwner] = useState("");
  const [folderName, setFolderName] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [billable, setBillable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (show && repository) {
      setRepositoryOwner(repository.repositoryOwner || "");
      setFolderName(repository.folderName || "");
      setDepartmentId(repository.departmentId ? String(repository.departmentId) : "");
      setBillable(!!repository.billable);
      setError("");
    }
  }, [show, repository]);

  if (!repository) return null;

  const selectableDepartments = departments.filter((d) => !d.isSystem);
  const selectedIsReal = selectableDepartments.some(
    (d) => String(d.departmentId) === departmentId,
  );
  // Repos still on "Dept Not Assigned" (or a department no longer listed) show it as the
  // current value, and saving stays blocked until a real department is chosen.
  const needsDepartment = !selectedIsReal;

  const handleBillableChange = (checked) => {
    if (checked) {
      confirmMarkBillable(() => setBillable(true));
    } else {
      setBillable(false);
    }
  };

  const handleSave = async () => {
    if (!repositoryOwner.trim() || !folderName.trim()) {
      setError("Repository owner and name are required.");
      return;
    }
    if (!selectedIsReal) {
      setError("Please select a department before saving.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await updateRepoData({
        repositoryId: repository.repositoryId,
        repositoryOwner: repositoryOwner.trim(),
        folderName: folderName.trim(),
        directoryPath: repository.directoryPath,
        clientId: repository.clientId,
        departmentId: Number(departmentId),
        billable,
      });
      await onSaved();
      onHide();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popup
      open={show}
      closeOnDocumentClick={false}
      onClose={onHide}
      contentStyle={{ width: "500px", borderRadius: "8px", padding: "20px" }}
    >
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h4>Edit Repository</h4>
          <Button
            bsStyle="link"
            onClick={onHide}
            style={{ fontSize: "1.2em" }}
            aria-label="Close"
          >
            &times;
          </Button>
        </div>
        <ControlLabel htmlFor="editRepoOwner">Repository Owner</ControlLabel>
        <FormControl
          id="editRepoOwner"
          type="text"
          value={repositoryOwner}
          onChange={(e) => setRepositoryOwner(e.target.value)}
          disabled={loading}
        />
        <ControlLabel htmlFor="editRepoName" style={{ marginTop: "12px" }}>
          Repository Name
        </ControlLabel>
        <FormControl
          id="editRepoName"
          type="text"
          value={folderName}
          onChange={(e) => setFolderName(e.target.value)}
          disabled={loading}
        />
        <ControlLabel htmlFor="editRepoDepartment" style={{ marginTop: "12px" }}>
          Department <span className="required-mark">*</span>
        </ControlLabel>
        <FormControl
          id="editRepoDepartment"
          componentClass="select"
          value={departmentId}
          onChange={(e) => setDepartmentId(e.target.value)}
          disabled={loading}
          aria-required="true"
          aria-describedby={needsDepartment ? "editRepoDepartment-notice" : undefined}
        >
          {needsDepartment && (
            <option value={departmentId} disabled>
              {repository.departmentName || "Select a department"}
            </option>
          )}
          {selectableDepartments.map((d) => (
            <option key={d.departmentId} value={String(d.departmentId)}>
              {d.departmentName}
            </option>
          ))}
        </FormControl>
        {needsDepartment && (
          <div id="editRepoDepartment-notice" className="notice-box">
            Please select a department before saving.
          </div>
        )}
        <div className="billable-field">
          <input
            type="checkbox"
            id="editRepoBillable"
            checked={billable}
            onChange={(e) => handleBillableChange(e.target.checked)}
            disabled={loading}
          />
          <label htmlFor="editRepoBillable">Billable</label>
        </div>
        {error && (
          <div className="field-error" role="alert">
            {error}
          </div>
        )}
        <div style={{ marginTop: "16px", textAlign: "right" }}>
          <Button onClick={onHide} disabled={loading} style={{ marginRight: "8px" }}>
            Cancel
          </Button>
          <Button
            className="brand-button"
            onClick={handleSave}
            disabled={loading || needsDepartment}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </Popup>
  );
}
